// What the number and the chart say about their data, apart from drawing it, so tests can check it without a
// browser: a change in words, and a period's peak and its average a day.

import { formatNumber } from "../../shared/format";
import type { Change } from "../../shared/period";
import type { Point, Stream } from "../../shared/types";

/** "5.6%", or the change itself when there is no percentage to give. */
export function deltaText(d: Change, format: Stream["format"], places = false): string {
  // a rate's change is in points (3.11% to 3.21% is 0.1 points, never "3%"); a ranking's, in places
  if (format?.style === "percent") return `${formatNumber(Math.abs(d.abs), { style: "number", decimals: Math.abs(d.abs) < 1 ? 2 : 1 })} pts`;
  if (places) return formatNumber(Math.abs(d.abs), format);
  if (d.pct !== null && Math.abs(d.pct) < 10000) return `${Math.abs(d.pct) >= 10 ? Math.abs(d.pct).toFixed(0) : Math.abs(d.pct).toFixed(1)}%`;
  // from nothing, or many times over: the difference itself, signed, so it never reads as the number before
  return `${d.abs < 0 ? "−" : "+"}${formatNumber(Math.abs(d.abs), format)}`;
}

/**
 * A period's days in two figures, its peak and its average a day, for a source that gives its own days (a visit, an
 * order, a click each day); a day still going counts in neither. Never for a rate or a ranking, nor a number an agent
 * sends, whose points may be levels rather than days.
 */
export function dayStats(s: Stream, points: Point[], rank: boolean): { peak: string; when: string; avg: string } | null {
  if (!s.period || rank || s.format?.style === "percent") return null;
  const today = Math.floor(Date.now() / 86_400_000) * 86_400_000;
  const days = points.filter((p) => p.t < today || s.period!.to < today);
  if (days.length < 3) return null;
  let top = days[0];
  let sum = 0;
  for (const p of days) {
    if (p.v > top.v) top = p;
    sum += p.v;
  }
  const avg = sum / days.length;
  return {
    peak: formatNumber(top.v, s.format),
    when: new Date(top.t).toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: "UTC" }),
    avg: formatNumber(avg, { ...(s.format ?? {}), style: s.format?.style ?? "number", decimals: Math.abs(avg) >= 10 ? 0 : 1 }),
  };
}
